/**
 * Accessibility Enhancements for AA ADA Compliance
 */

class AccessibilityEnhancements {
  constructor() {
    this.init();
  }

  init() {
    this.setupScreenReaderSupport();
    this.setupFocusManagement();
    this.setupFormValidation();
    this.setupImageAltText();
    this.setupLiveRegions();
  }

  /**
   * Enhanced screen reader support
   */
  setupScreenReaderSupport() {
    // Use existing live region from theme.liquid
    // No need to create duplicate

    // Announce page changes
    this.announceToScreenReader = (message, priority = 'polite') => {
      const liveRegion = document.getElementById('live-region');
      if (liveRegion) {
        liveRegion.textContent = message;
        liveRegion.setAttribute('aria-live', priority);
      }
    };

    // Make announcement function globally available
    window.announceToScreenReader = this.announceToScreenReader;
  }


  /**
   * Enhanced focus management
   */
  setupFocusManagement() {
    // Trap focus in modals
    this.trapFocus = (element) => {
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [role="button"], [role="tab"], [role="option"]';
      const focusableContent = element.querySelectorAll(focusableElements);
      const firstFocusableElement = focusableContent[0];
      const lastFocusableElement = focusableContent[focusableContent.length - 1];

      element.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              lastFocusableElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              firstFocusableElement.focus();
              e.preventDefault();
            }
          }
        }
      });
    };

    // Make trapFocus globally available
    window.trapFocus = this.trapFocus;
  }

  /**
   * Enhanced form validation
   */
  setupFormValidation() {
    // Form error announcements
    this.announceFormError = (field, message) => {
      const errorId = field.id + '-error';
      let errorElement = document.getElementById(errorId);
      
      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.id = errorId;
        errorElement.className = 'field-error sr-only';
        errorElement.setAttribute('role', 'alert');
        field.parentNode.appendChild(errorElement);
      }
      
      errorElement.textContent = message;
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', errorId);
      
      this.announceToScreenReader(`Error: ${message}`, 'assertive');
    };

    // Clear form errors
    this.clearFormError = (field) => {
      const errorId = field.id + '-error';
      const errorElement = document.getElementById(errorId);
      if (errorElement) {
        errorElement.textContent = '';
      }
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    };

    // Make form functions globally available
    window.announceFormError = this.announceFormError;
    window.clearFormError = this.clearFormError;
  }

  /**
   * Enhanced image alt text handling
   */
  setupImageAltText() {
    // Only process images that are missing alt attributes
    const images = document.querySelectorAll('img:not([alt])');
    if (images.length === 0) return;
    
    images.forEach(img => {
      // Skip if already processed
      if (img.hasAttribute('data-alt-processed')) return;
      
      img.alt = 'Decorative image';
      img.setAttribute('aria-hidden', 'true');
      img.setAttribute('data-alt-processed', 'true');
    });
  }

  /**
   * Setup live regions for dynamic content
   */
  setupLiveRegions() {
    // Live regions are already created in theme.liquid
    // No additional setup needed
  }

  /**
   * Enhanced button labeling
   */
  enhanceButtons() {
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach(button => {
      if (!button.textContent.trim() && !button.querySelector('svg')) {
        button.setAttribute('aria-label', 'Button');
      }
    });
  }

}

// Initialize accessibility enhancements when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Use requestIdleCallback for non-critical accessibility enhancements
  if (window.requestIdleCallback) {
    requestIdleCallback(() => new AccessibilityEnhancements());
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => new AccessibilityEnhancements(), 0);
  }
});

// Export for use in other scripts
window.AccessibilityEnhancements = AccessibilityEnhancements;

/** Warn before opening a new tab, including links inserted by theme sections. */
(() => {
  const warning = '(opens in a new tab)';
  function warnNewTabLinks(root) {
    const links = [...root.querySelectorAll('a[target="_blank"]')];
    if (root.matches?.('a[target="_blank"]')) links.unshift(root);
    links.forEach((link) => {
      if (link.querySelector('[data-new-tab-warning]')) return;
      const label = link.getAttribute('aria-label');
      const labelledBy = link.getAttribute('aria-labelledby');
      const existingName = labelledBy
        ? labelledBy.split(/\s+/).map((id) => document.getElementById(id)?.textContent || '').join(' ')
        : label || link.textContent.trim() || [...link.querySelectorAll('img[alt]')]
          .map((image) => image.getAttribute('alt').trim()).filter(Boolean).join(' ') || link.getAttribute('title') || '';
      // A destination must already be named; a warning cannot replace its identity.
      if (!existingName.trim()) return;
      if (/opens? in (?:a )?new (?:tab|window)/i.test(existingName)) return;

      const notice = document.createElement('span');
      notice.dataset.newTabWarning = '';
      notice.className = 'visually-hidden';
      notice.textContent = ` ${warning}`;
      // Preserve the layout of badges/buttons while including the warning in the name.
      if (labelledBy) {
        notice.id = 'new-tab-' + Math.random().toString(36).slice(2, 11);
        link.setAttribute('aria-labelledby', `${labelledBy} ${notice.id}`);
      } else if (label || existingName === link.getAttribute('title')) {
        link.setAttribute('aria-label', `${existingName} ${warning}`);
      }
      link.append(notice);
    });
  }

  function initNewTabWarnings() {
    warnNewTabLinks(document);
    new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') warnNewTabLinks(record.target);
        for (const node of record.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) warnNewTabLinks(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['target'] });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNewTabWarnings, { once: true });
  } else {
    initNewTabWarnings();
  }
})();
